import React, { useEffect, useRef, useState } from "react";
import { X, AlertCircle } from "lucide-react";

const VideoModal = ({ isOpen, onClose, videoSrc }) => {
    const containerRef = useRef(null);
    const videoRef = useRef(null);
    const [videoError, setVideoError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Normalize video source path
    const normalizedVideoSrc = React.useMemo(() => {
        if (!videoSrc) return '';
        // Ensure it starts with / if it's a relative path
        const basePath = videoSrc.startsWith('/') ? '' : '/';
        // Use PUBLIC_URL if available (for Create React App)
        const publicUrl = process.env.PUBLIC_URL || '';
        return `${publicUrl}${basePath}${videoSrc}`;
    }, [videoSrc]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
            // Focus the video container for keyboard navigation
            if (containerRef.current) {
                containerRef.current.focus();
            }
            // Reset error state when modal opens
            setVideoError(false);
            setIsLoading(true);
            // Auto-play video when modal opens
            if (videoRef.current) {
                // Reload video to ensure it's fresh
                videoRef.current.load();

                // Try to play after a short delay to ensure video is loaded
                setTimeout(() => {
                    if (videoRef.current) {
                        videoRef.current.play().catch(err => {
                            console.log("Autoplay prevented:", err);
                            // If autoplay fails, just show controls - user can click play
                        });
                    }
                }, 100);
            }
        } else {
            document.body.style.overflow = "unset";
            // Pause video when modal closes
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
            }
            setIsLoading(true);
            setVideoError(false);
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen, videoSrc]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === "Escape" && isOpen) {
                onClose();
            }
        };
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("keydown", handleEscape);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                ref={containerRef}
                tabIndex={-1}
                className="relative w-full max-w-6xl mx-4 bg-black rounded-lg overflow-hidden shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                style={{ maxHeight: '90vh' }}
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-20 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-lg focus:outline-none focus:ring-2 focus:ring-white"
                    aria-label="Close video"
                >
                    <X className="w-6 h-6 text-gray-900" />
                </button>
                {videoError ? (
                    <div className="flex flex-col items-center justify-center p-12 text-white min-h-[400px]">
                        <AlertCircle className="w-16 h-16 mb-4 text-gray-400" />
                        <h3 className="text-xl font-bold mb-2">Video Not Available</h3>
                        <p className="text-gray-400 text-center max-w-md">
                            The video file could not be loaded. Please ensure the video file is properly deployed or hosted.
                        </p>
                        <button
                            onClick={onClose}
                            className="mt-6 px-6 py-2 bg-primary hover:bg-primary/90 rounded-lg font-semibold transition-colors"
                        >
                            Close
                        </button>
                    </div>
                ) : (
                    <video
                        ref={videoRef}
                        className="w-full h-auto max-h-[90vh]"
                        controls
                        autoPlay
                        playsInline
                        controlsList="nodownload"
                        style={{ display: 'block' }}
                        onError={(e) => {
                            const video = videoRef.current;
                            console.error("Video error:", e);
                            console.error("Original video src:", videoSrc);
                            console.error("Normalized video src:", normalizedVideoSrc);
                            console.error("Full video URL:", video?.src || normalizedVideoSrc);
                            console.error("Video element error:", video?.error);
                            console.error("Error code:", video?.error?.code);
                            console.error("Error message:", video?.error?.message);
                            console.error("Video network state:", video?.networkState);
                            console.error("Video ready state:", video?.readyState);

                            // Check if it's a network error (file not found)
                            if (video?.error?.code === 4 || video?.networkState === 3) {
                                console.error("Network error - video file may not be accessible at:", normalizedVideoSrc);
                                console.error("Trying to verify file accessibility...");
                                // Try to fetch the file to verify it exists
                                fetch(normalizedVideoSrc, { method: 'HEAD' })
                                    .then(res => {
                                        console.log("File fetch response:", res.status, res.statusText);
                                        if (!res.ok) {
                                            console.error("File not found or not accessible. Status:", res.status);
                                        }
                                    })
                                    .catch(err => {
                                        console.error("File fetch failed:", err);
                                    });
                            }

                            setVideoError(true);
                            setIsLoading(false);
                        }}
                        onLoadedData={() => {
                            console.log("Video loaded successfully");
                            setIsLoading(false);
                        }}
                        onCanPlay={() => {
                            console.log("Video can play");
                            setIsLoading(false);
                        }}
                        onLoadStart={() => {
                            console.log("Video load started:", normalizedVideoSrc);
                        }}
                    >
                        {/* Try MP4 first (better browser support), then fallback to MOV */}
                        <source src={normalizedVideoSrc.replace(/\.mov$/i, '.mp4')} type="video/mp4" />
                        <source src={normalizedVideoSrc.replace(/ /g, '%20').replace(/\.mov$/i, '.mp4')} type="video/mp4" />
                        <source src={encodeURI(normalizedVideoSrc.replace(/\.mov$/i, '.mp4'))} type="video/mp4" />
                        {/* Fallback to MOV if MP4 not available */}
                        <source src={normalizedVideoSrc} type="video/quicktime" />
                        <source src={normalizedVideoSrc.replace(/ /g, '%20')} type="video/quicktime" />
                        <source src={encodeURI(normalizedVideoSrc)} type="video/quicktime" />
                        Your browser does not support the video tag.
                    </video>
                )}
                {isLoading && !videoError && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <div className="text-white text-center">
                            <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-sm">Loading video...</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VideoModal;

